'use client';

import { useEffect, useRef } from 'react';
import { trackEvent } from '@/lib/analytics/matomo';

interface SectionTrackerProps {
  /** Section name for the Matomo event (e.g. 'hero', 'how-it-works') */
  section: string;
  children: React.ReactNode;
}

/**
 * Wraps a section and fires a `section_scroll_depth` Matomo event
 * the first time the section scrolls into view.
 */
export default function SectionTracker({ section, children }: SectionTrackerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const tracked = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !tracked.current) {
          tracked.current = true;
          trackEvent('landing', 'section_scroll_depth', section);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [section]);

  return <div ref={ref}>{children}</div>;
}
